/**
 * Mindspace-specific hooks for API interactions
 *
 * Provides hooks for:
 * - Graph data (via React Query with caching and background updates)
 * - Last updated timestamp
 * - Concept details
 * - Manual extraction trigger
 */

import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMindspaceData } from '@/hooks/useDataProvider';
import type { ConceptDetail } from '@/types/mindspace-concept';
import { mindspaceApi } from '@/lib/api-client';
import type {
  KnowledgeGraphFilters,
  KnowledgeGraphResponse,
} from '@/lib/api-client';
// Dynamic, not static: these fixtures are only reachable in mock/empty mode,
// and every use below sits inside an async queryFn.
import { loadMockConceptDetails, loadMockPracticeData } from '@/data';
import { useDataMode } from '@/contexts/DataModeContext';
import { validateGraphData } from '@/utils/graphValidation';

// ============================================================================
// TYPES
// ============================================================================

export interface LastUpdatedInfo {
  last_run_at: string | null;
  status: string;
  lessons_processed: number;
  concepts_created: number;
  concepts_merged: number;
  edges_created: number;
}

export interface GraphNode {
  id: string;
  label: string;
  category: string;
  importance: number;
  sourceLessons: Array<{
    lessonId: string;
    title: string;
    learnedAt: string;
  }>;
  x: number;
  y: number;
}

export interface GraphEdge {
  from: string;
  to: string;
  type: string;
  weight: number;
}

export interface MindspaceGraphData {
  nodes: GraphNode[];
  links: GraphEdge[];
  lastUpdated?: string;
}

// ============================================================================
// QUERY KEYS
// ============================================================================

/**
 * Query keys for mindspace data
 * Used for cache management and invalidation
 */
export const mindspaceKeys = {
  all: ['mindspace'] as const,
  graph: () => [...mindspaceKeys.all, 'graph'] as const,
  knowledgeArchiveGraph: (filters: KnowledgeGraphFilters | undefined) =>
    [...mindspaceKeys.all, 'knowledgeArchiveGraph', filters ?? {}] as const,
  sourceLessons: () => [...mindspaceKeys.all, 'sourceLessons'] as const,
  sourceDocuments: () => [...mindspaceKeys.all, 'sourceDocuments'] as const,
  domainTagOptions: () => [...mindspaceKeys.all, 'domainTagOptions'] as const,
  userTagOptions: () => [...mindspaceKeys.all, 'userTagOptions'] as const,
  lastUpdated: () => [...mindspaceKeys.all, 'lastUpdated'] as const,
  concept: (id: string) => [...mindspaceKeys.all, 'concept', id] as const,
};

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Hook for fetching mindspace graph data (mode-aware).
 * Respects VITE_DATA_MODE (empty/mock/api) and falls back to mock data when
 * the API is unsupported. Prefer this over useMindspaceGraph, which always
 * hits the API and returns empty data when the backend is unavailable.
 */
export { useMindspaceData };

/**
 * Hook for fetching mindspace graph data with React Query
 *
 * Features:
 * - Automatic caching (fresh for 5 minutes, kept in cache for 30 minutes)
 * - Background refetch every 15 minutes
 * - Refetch when window regains focus
 * - Stale-while-revalidate pattern (shows cached data while fetching)
 *
 * @returns Query result with nodes, links, loading state, and error
 */
export function useMindspaceGraph() {
  return useQuery({
    queryKey: mindspaceKeys.graph(),
    queryFn: async () => {
      const data = await mindspaceApi.getGraph();

      // Validate graph data to filter out invalid links
      // This prevents react-d3-graph from crashing when links reference non-existent nodes
      const rawGraphData = {
        nodes: data.nodes || [],
        links: data.links || [],
      };

      // validateGraphData is generic, so the validated result keeps the real
      // getGraph() node/link types. Returning it directly (no `unknown` cast)
      // means an API shape drift surfaces as a type error here instead of an
      // undefined-read crash inside the graph renderer.
      const validatedData = validateGraphData(rawGraphData);

      return {
        ...validatedData,
        lastUpdated: data.last_updated ?? undefined,
      };
    },
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes (renamed from cacheTime)
    refetchInterval: 15 * 60 * 1000, // Background refetch every 15 minutes
    refetchOnWindowFocus: true, // Refetch when tab regains focus
    retry: 2, // Retry failed requests twice
  });
}

/**
 * Hook to get query client for manual cache operations
 *
 * Usage:
 * ```tsx
 * const { invalidateMindspace, prefetchMindspace } = useMindspaceActions()
 *
 * // After manual extraction trigger
 * await invalidateMindspace()
 *
 * // Prefetch before navigation
 * await prefetchMindspace()
 * ```
 */
export function useMindspaceActions() {
  const queryClient = useQueryClient();

  return {
    /**
     * Invalidate mindspace cache and trigger refetch
     * Use after manual concept extraction or known data changes
     */
    invalidateMindspace: () => {
      return queryClient.invalidateQueries({ queryKey: mindspaceKeys.all });
    },

    /**
     * Prefetch mindspace graph data
     * Use to load data before user navigates to mindspace page
     */
    prefetchMindspace: () => {
      return queryClient.prefetchQuery({
        queryKey: mindspaceKeys.graph(),
        queryFn: async () => {
          const data = await mindspaceApi.getGraph();

          // Apply validation to filter out invalid links
          const rawGraphData = {
            nodes: data.nodes || [],
            links: data.links || [],
          };

          const validatedData = validateGraphData(rawGraphData);

          return {
            ...validatedData,
            lastUpdated: data.last_updated ?? undefined,
          };
        },
      });
    },

    /**
     * Get cached mindspace data without triggering a fetch
     */
    getCachedMindspace: () => {
      return queryClient.getQueryData<MindspaceGraphData>(
        mindspaceKeys.graph()
      );
    },
  };
}

/**
 * Hook for fetching last updated timestamp
 */
export function useLastUpdated() {
  const [data, setData] = useState<LastUpdatedInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchLastUpdated = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const result = await mindspaceApi.getLastUpdated();
        setData(result);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Unknown error'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchLastUpdated();
  }, []);

  return { data, isLoading, error };
}

/**
 * Hook for fetching concept details
 * Supports empty, mock, and API data modes
 */
export function useConceptDetail(conceptId: string | null) {
  // Backed by React Query so any mutation in the sidebar (e.g. the
  // user-tags PATCH inside `UserTagEditor`) can invalidate
  // `mindspaceKeys.concept(id)` and the panel auto-refreshes.
  const { isEmpty, isMock, isApi } = useDataMode();

  const query = useQuery<ConceptDetail | null>({
    queryKey: mindspaceKeys.concept(conceptId ?? ''),
    enabled: !!conceptId,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 1,
    queryFn: async () => {
      if (!conceptId) return null;
      if (isEmpty) return null;

      // Resolution order:
      //   1. Legacy mockConceptDetails map (graph nodes like `math`, etc.)
      //   2. Practice-mock concepts synthesized into the ConceptDetail shape
      //      (covers `mock-quadratic` etc. coming from the new mock graph)
      // Throwing on miss was the source of "Failed to load" - we now fall
      // through to a synthesized detail instead.
      const resolveMock = async (): Promise<ConceptDetail | null> => {
        const legacy = (await loadMockConceptDetails()).getMockConceptDetail(
          conceptId
        );
        if (legacy) return legacy;
        const synth = (
          await loadMockPracticeData()
        ).synthesizeConceptDetailFromMock(conceptId);
        return (synth as ConceptDetail | null) ?? null;
      };

      if (isMock) {
        await new Promise(resolve => setTimeout(resolve, 100));
        return await resolveMock();
      }
      if (isApi) {
        try {
          return await mindspaceApi.getConcept(conceptId);
        } catch (err) {
          // API mode falls back to mock data so dev environments with a
          // disconnected backend still render *something* in the sidebar.
          console.warn(
            '[useConceptDetail] API failed, falling back to mock',
            err
          );
          return await resolveMock();
        }
      }
      return await resolveMock();
    },
  });

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
  };
}

/**
 * Hook for triggering manual extraction (for testing)
 */
export function useTriggerExtraction() {
  const queryClient = useQueryClient();
  const [isTriggering, setIsTriggering] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [taskId, setTaskId] = useState<string | null>(null);

  const trigger = async () => {
    try {
      setIsTriggering(true);
      setError(null);

      const result = await mindspaceApi.triggerExtraction();
      setTaskId(result.task_id);
      // The fan-out resync runs async on Celery - we can't know the
      // exact moment new concepts land. Invalidating every
      // `mindspace`-prefixed query (graph, options, daily plan, concept
      // details) means the next render after sync completes picks up
      // fresh data without forcing a hard refresh.
      queryClient.invalidateQueries({ queryKey: mindspaceKeys.all });
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      setError(error);
      throw error;
    } finally {
      setIsTriggering(false);
    }
  };

  return { trigger, isTriggering, error, taskId };
}

// ============================================================================
// KNOWLEDGE ARCHIVE HOOKS - Step 3 additions
// ============================================================================

/**
 * Filtered Knowledge Archive graph fetch.
 *
 * Keyed on the filter object so React Query manages cache slots per
 * unique view. Composite top-N ranking is applied server-side, so the
 * frontend just renders whatever the API returns plus the
 * `total_concepts` / `capped` metadata.
 */
export function useKnowledgeArchiveGraph(filters?: KnowledgeGraphFilters) {
  const { isMock, isEmpty } = useDataMode();
  return useQuery<KnowledgeGraphResponse>({
    queryKey: mindspaceKeys.knowledgeArchiveGraph(filters),
    queryFn: async () => {
      if (isEmpty) {
        return {
          nodes: [],
          links: [],
          last_updated: null,
          total_concepts: 0,
          capped: false,
        };
      }
      if (isMock)
        return (await loadMockPracticeData()).buildMockKnowledgeGraph();
      try {
        return await mindspaceApi.getGraph(filters);
      } catch (err) {
        console.warn(
          '[useKnowledgeArchiveGraph] API failed, falling back to mock',
          err
        );
        return (await loadMockPracticeData()).buildMockKnowledgeGraph();
      }
    },
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });
}

/**
 * Hard-delete a concept (mutation). On success, invalidates archive
 * queries so the deleted node falls out of the rendered graph.
 */
export function useDeleteConcept() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (conceptId: string) => mindspaceApi.deleteConcept(conceptId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mindspaceKeys.all });
    },
  });
}

/**
 * Pause / resume a concept ("archive" toggle). Archiving drops the concept
 * from the revision pool (daily plan + practice queue) and hides it from the
 * default graph; unarchiving restores both. FSRS history is preserved.
 *
 * Invalidates every `mindspace`-prefixed query (graph, daily plan, and the
 * concept detail) so the graph re-renders without the node and the sidebar
 * reflects the new paused state.
 */
export function useSetConceptArchived() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      conceptId,
      archived,
    }: {
      conceptId: string;
      archived: boolean;
    }) =>
      archived
        ? mindspaceApi.archiveConcept(conceptId)
        : mindspaceApi.unarchiveConcept(conceptId),
    onSuccess: (_data, { conceptId }) => {
      queryClient.invalidateQueries({ queryKey: mindspaceKeys.all });
      queryClient.invalidateQueries({
        queryKey: mindspaceKeys.concept(conceptId),
      });
    },
  });
}
